import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit2, Trash2, Eye, ExternalLink, Briefcase, ChevronRight } from "lucide-react";
import { http, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending:  { cls: "bg-yellow-50 text-yellow-700 border-yellow-200", label: "Pending Review" },
  approved: { cls: "bg-green-50 text-green-700 border-green-200", label: "Live" },
  rejected: { cls: "bg-red-50 text-red-700 border-red-200", label: "Rejected" },
  suspended:{ cls: "bg-gray-50 text-gray-600 border-gray-200", label: "Suspended" },
};

export default function MyListings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/auth", { replace: true }); return; }
    http.get("/me/marketplace/listings")
      .then(r => setListings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const del = async (id) => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    try {
      await http.delete(`/marketplace/listings/${id}`);
      toast.success("Listing deleted");
      setListings(l => l.filter(x => x.id !== id));
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>My Listings</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your business and service listings</p>
        </div>
        <Link to="/marketplace/create" className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: "#0F1E38" }}>
          <Plus size={15} /> New Listing
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse">{[1,2].map(i => <div key={i} className="h-28 rounded-2xl bg-gray-100" />)}</div>
      ) : listings.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
          <Briefcase size={48} className="mx-auto text-gray-200 mb-4" />
          <h2 className="text-xl font-bold text-gray-700 mb-2">No listings yet</h2>
          <p className="text-gray-500 text-sm mb-6">Create your first business or service listing to start connecting with the community.</p>
          <Link to="/marketplace/create" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white" style={{ background: "#0F1E38" }}>
            <Plus size={16} /> Create a Listing
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map(l => {
            const status = STATUS_STYLES[l.status] || STATUS_STYLES.pending;
            return (
              <div key={l.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex gap-4 p-4">
                  {/* Image */}
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-50 shrink-0">
                    {l.logo_url
                      ? <img src={l.logo_url} alt={l.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full grid place-items-center text-xl font-bold" style={{ background: "#0F1E38", color: "#C5A028" }}>{(l.name || "?")[0]}</div>
                    }
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-bold text-gray-900 text-base leading-tight">{l.name}</div>
                        {l.category_name && <div className="text-xs text-yellow-700 font-semibold mt-0.5">{l.category_name}</div>}
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${status.cls}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />{status.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{l.tagline || l.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      {l.location && <span>{l.location}</span>}
                      <span>{new Date(l.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                      {l.review_count > 0 && <span>⭐ {l.avg_rating?.toFixed(1)} ({l.review_count})</span>}
                    </div>
                  </div>
                </div>

                {/* Status message */}
                {l.status === "rejected" && l.rejection_reason && (
                  <div className="mx-4 mb-3 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
                    <strong>Rejection reason:</strong> {l.rejection_reason}
                  </div>
                )}
                {l.status === "pending" && (
                  <div className="mx-4 mb-3 p-3 bg-yellow-50 border border-yellow-100 rounded-xl text-xs text-yellow-700">
                    Your listing is under review. This usually takes 24–48 hours.
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1 px-4 pb-4 border-t border-gray-100 pt-3">
                  {l.status === "approved" && (
                    <Link to={`/marketplace/business/${l.id}`} target="_blank" className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-blue-600 hover:bg-blue-50 transition-colors">
                      <ExternalLink size={13} /> View Live
                    </Link>
                  )}
                  <Link to={`/marketplace/create/${l.id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                    <Edit2 size={13} /> Edit
                  </Link>
                  <button onClick={() => del(l.id)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors ml-auto">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tips */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
        <div className="font-bold text-gray-900 text-sm">Tips for a great listing</div>
        {["Use a clear, professional profile photo", "Add detailed service descriptions with pricing", "Include your location and contact details", "Upload gallery photos of your work", "Respond quickly to inquiries for better rankings"].map((tip, i) => (
          <div key={i} className="flex items-start gap-2 text-sm text-gray-600"><span className="text-yellow-500 mt-0.5">•</span>{tip}</div>
        ))}
      </div>
    </div>
  );
}
